import { Badge, Code } from "@one-nexty/components-web";

import { Section, SubSection } from "../ui/section";
import { useCopy } from "../lib/use-copy";
import {
  colorFamilies,
  fontFamilies,
  fontSizes,
  fontWeights,
  leaf,
  radii,
  semanticColors,
  shadows,
  spacings,
} from "../lib/tokens";

/* ==========================================================================
   Couleurs
   ========================================================================== */

export function ColorsSection() {
  const { copy, copied } = useCopy();

  return (
    <Section
      id="couleurs"
      eyebrow="Fondations"
      title="Couleurs"
      description="Palette en OKLCH, perceptuellement uniforme : à luminosité égale, deux teintes paraissent aussi claires. Le repli hexadécimal est calculé par le pipeline."
    >
      <SubSection title="Palette primitive" hint="Cliquez sur une nuance pour copier son nom">
        <div className="space-y-5">
          {colorFamilies.map(({ name, shades }) => (
            <div key={name}>
              <div className="mb-2 flex items-baseline gap-2">
                <h4 className="font-mono text-xs font-medium text-fg">{name}</h4>
                <span className="text-xs text-fg-muted">{shades.length} nuances</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {shades.map((token) => (
                  <button
                    key={token.name}
                    type="button"
                    onClick={() => void copy(token.name)}
                    title={`${token.name}\n${token.value}\n${token.hex ?? ""}`}
                    className="group w-[72px] text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    <span
                      className="block h-12 w-full transform-gpu rounded-md border border-border transition-transform duration-fast ease-standard group-hover:-translate-y-0.5"
                      style={{ backgroundColor: token.value }}
                    />
                    <span className="mt-1 block font-mono text-[10px] text-fg-muted">
                      {copied === token.name ? "copié ✓" : leaf(token)}
                    </span>
                    <span className="block font-mono text-[10px] text-fg-muted">{token.hex}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SubSection>

      <SubSection
        title="Tokens sémantiques"
        hint="La seule couche que les composants ont le droit de consommer"
      >
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-3xl border-collapse text-left text-sm">
            <thead className="bg-surface-sunken text-xs tracking-wide text-fg-muted uppercase">
              <tr>
                <th className="px-4 py-2.5 font-medium">Token</th>
                <th className="px-4 py-2.5 font-medium">Utilitaire</th>
                <th className="px-4 py-2.5 font-medium">Clair</th>
                <th className="px-4 py-2.5 font-medium">Sombre</th>
                <th className="px-4 py-2.5 font-medium">Usage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {semanticColors.map((token) => (
                <tr key={token.name}>
                  <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap text-fg">
                    {leaf(token)}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <Code>bg-{leaf(token)}</Code>
                  </td>
                  <td className="px-4 py-2.5">
                    <Swatch value={token.value} hex={token.hex} />
                  </td>
                  <td className="px-4 py-2.5">
                    <Swatch value={token.darkValue ?? token.value} hex={token.darkHex ?? token.hex} />
                  </td>
                  <td className="px-4 py-2.5 text-xs text-fg-muted">{token.description ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SubSection>
    </Section>
  );
}

function Swatch({ value, hex }: { value: string; hex?: string }) {
  return (
    <span className="flex items-center gap-2 whitespace-nowrap">
      <span
        className="inline-block size-5 shrink-0 rounded-sm border border-border"
        style={{ backgroundColor: value }}
      />
      <span className="font-mono text-[11px] text-fg-muted">{hex ?? value}</span>
    </span>
  );
}

/* ==========================================================================
   Typographie
   ========================================================================== */

export function TypographySection() {
  return (
    <Section
      id="typographie"
      eyebrow="Fondations"
      title="Typographie"
      description="Inter pour le texte, JetBrains Mono pour le code. Chaque taille embarque sa hauteur de ligne : text-lg applique aussi son leading, sans classe supplémentaire."
    >
      <SubSection title="Familles">
        <div className="grid gap-3 sm:grid-cols-3">
          {fontFamilies.map((token) => (
            <div key={token.name} className="rounded-xl border border-border p-4">
              <p className="font-mono text-xs text-fg-muted">font-{leaf(token)}</p>
              <p className="mt-2 text-2xl text-fg" style={{ fontFamily: token.value }}>
                Ag 123
              </p>
              <p className="mt-2 truncate text-[11px] text-fg-muted">{token.value}</p>
            </div>
          ))}
        </div>
      </SubSection>

      <SubSection title="Échelle">
        <div className="divide-y divide-border rounded-xl border border-border">
          {fontSizes.map((token) => (
            <div key={token.name} className="flex flex-wrap items-baseline gap-x-5 gap-y-1 px-5 py-3">
              <span className="w-24 shrink-0">
                <Code>text-{leaf(token)}</Code>
              </span>
              <span className="w-28 shrink-0 font-mono text-[11px] text-fg-muted">
                {token.value}
                {token.lineHeight && ` / ${token.lineHeight}`}
              </span>
              <p
                className="min-w-0 flex-1 truncate tracking-tight text-fg"
                style={{ fontSize: token.value, lineHeight: token.lineHeight }}
              >
                Le design system est un produit
              </p>
            </div>
          ))}
        </div>
      </SubSection>

      <SubSection title="Graisses">
        <div className="flex flex-wrap gap-2">
          {fontWeights.map((token) => (
            <div key={token.name} className="rounded-xl border border-border px-4 py-3">
              <p className="text-lg text-fg" style={{ fontWeight: token.value }}>
                Nexty
              </p>
              <p className="mt-1 font-mono text-[11px] text-fg-muted">
                font-{leaf(token)} · {token.value}
              </p>
            </div>
          ))}
        </div>
      </SubSection>
    </Section>
  );
}

/* ==========================================================================
   Espacements
   ========================================================================== */

export function SpacingSection() {
  return (
    <Section
      id="espacements"
      eyebrow="Fondations"
      title="Espacements"
      description="Grille de 4 px. spacing.base alimente l'échelle dynamique de Tailwind v4 : p-7 ou gap-13 restent valides sans token dédié."
    >
      <div className="space-y-1.5">
        {spacings.map((token) => (
          <div key={token.name} className="flex items-center gap-4">
            <span className="w-10 shrink-0 font-mono text-xs text-fg-muted">{leaf(token)}</span>
            <span className="w-14 shrink-0 font-mono text-[11px] text-fg-muted">{token.value}</span>
            <span className="h-3.5 rounded-sm bg-accent" style={{ width: token.value }} />
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ==========================================================================
   Rayons & élévations
   ========================================================================== */

export function ElevationSection() {
  return (
    <Section
      id="elevation"
      eyebrow="Fondations"
      title="Rayons & élévations"
      description="Rayons serrés, ombres quasi absentes : la hiérarchie passe par les bordures fines et les fonds."
    >
      <SubSection title="Rayons">
        <div className="flex flex-wrap gap-3">
          {radii.map((token) => (
            <div key={token.name} className="text-center">
              <div
                className="size-16 border border-accent bg-accent-subtle"
                style={{ borderRadius: token.value }}
              />
              <p className="mt-1.5 font-mono text-[11px] text-fg-muted">rounded-{leaf(token)}</p>
              <p className="font-mono text-[10px] text-fg-muted">{token.value}</p>
            </div>
          ))}
        </div>
      </SubSection>

      <SubSection title="Ombres portées">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shadows
            .filter((token) => !leaf(token).endsWith("-inset"))
            .map((token) => (
              <div
                key={token.name}
                className="rounded-xl border border-border bg-surface-raised p-4"
                style={{ boxShadow: token.value }}
              >
                <Badge tone="brand">shadow-{leaf(token)}</Badge>
                <p className="mt-2.5 text-[11px] text-fg-muted">{token.description}</p>
              </div>
            ))}
        </div>
      </SubSection>

      <SubSection
        title="Ombres internes"
        hint="Namespace distinct : elles se composent avec une ombre portée"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {shadows
            .filter((token) => leaf(token).endsWith("-inset"))
            .map((token) => {
              // Le token s'appelle `shadow.raised-inset`, l'utilitaire Tailwind
              // `inset-shadow-raised` : deux namespaces distincts.
              const utility = `inset-shadow-${leaf(token).replace(/-inset$/, "")}`;
              const onAccent = leaf(token).startsWith("raised");

              return (
                <div key={token.name} className="rounded-xl border border-border p-4">
                  <Badge tone="brand">{utility}</Badge>
                  <p className="mt-2.5 text-[11px] text-fg-muted">{token.description}</p>

                  {/* L'arête n'existe que posée sur un aplat. */}
                  <div
                    className={
                      onAccent
                        ? "mt-3 rounded-md bg-accent px-3 py-2 text-center text-xs font-medium text-fg-on-accent"
                        : "mt-3 rounded-md bg-surface-sunken px-3 py-2 text-center text-xs font-medium text-fg-muted"
                    }
                    style={{ boxShadow: token.value }}
                  >
                    {onAccent ? "Aplat de marque" : "Champ de saisie"}
                  </div>
                </div>
              );
            })}
        </div>
      </SubSection>
    </Section>
  );
}
